<template>
    <div class="columns">
        <div class="column is-12 pb-0">
            <div class="mb-3 is-flex is-justify-content-start is-flex-wrap-wrap is-align-items-center" v-if="totalMats > 0">
                <label class="label mb-0 mr-2">
                    {{ translate("admin_schedule_manage.conflicts_threshold") }}
                </label>
                <div class="select is-small mr-2">
                    <select v-model="conflictsThreshold" @change="onConflictsThresholdChange">
                        <option :value="0">0 {{ translate("common.minutes") }}</option>
                        <option :value="300">5 {{ translate("common.minutes") }}</option>
                        <option :value="900">15 {{ translate("common.minutes") }}</option>
                        <option :value="1800">30 {{ translate("common.minutes") }}</option>
                        <option :value="3600">60 {{ translate("common.minutes") }}</option>
                    </select>
                </div>
                <p v-if="conflictsArePresent">
                    <a class="has-text-warning" @click="openConflictsDetailsModal">
                        <span class="icon">
                            <i class="fas fa-exclamation-triangle"></i>
                        </span>
                        <span>{{ translate("admin_schedule_manage.conflicts_present") }}</span>
                    </a>
                </p>
            </div>

            <div class="is-flex is-align-items-center is-flex-wrap-wrap" v-if="totalMats > 0">
                <div class="mr-3">
                    <Checkbox2 v-model="multiRowMode">
                        {{ translate("admin_schedule_manage.multi_row_mode") }}
                    </Checkbox2>
                </div>
                <div class="mr-3">|</div>

                <div class="mr-3" v-for="matSchedule in matSchedules" :key="matSchedule.id">
                    <Checkbox2 v-model="matVisibility[matSchedule.id]" @input="onMatVisibilityUpdated">
                        {{ matSchedule.name }}
                    </Checkbox2>
                </div>

                <button class="button is-small" v-if="allMatsSelected" @click="deselectAllMats">
                    <span class="icon">
                        <i class="fas fa-times"></i>
                    </span>
                    <span>
                        {{ translate("common.deselect_all") }}
                    </span>
                </button>
                <button class="button is-small" @click="selectAllMats" v-else>
                    <span class="icon">
                        <i class="fas fa-check"></i>
                    </span>
                    <span>
                        {{ translate("common.select_all") }}
                    </span>
                </button>
            </div>

            <div class="notification is-info is-light mt-3" v-if="totalMats > 0 && !editMode">
                <span class="icon">
                    <i class="fas fa-lock"></i>
                </span>
                <span>{{ translate("admin_schedule_manage.read_only_banner") }}</span>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { computed } from "vue";
    import { translate } from "../../Common/translations";
    import { useScheduleMainStore } from "./useScheduleMainStore";
    import { useShowScheduleStore } from "./useShowScheduleStore";
    import Checkbox2 from "../../Common/Checkbox2.vue";
    import { eventBus, ON_SHOW_CONFLICTS_MODAL } from "./eventBus";
    import {
        saveConflictsThresholdToLocalStorage,
        saveScheduleMatsMultiRowModeToLocalStorage,
        saveVisibleMatsToLocalStorage,
    } from "./scheduleManageApp";

    // Emits
    const emit = defineEmits<{
        conflictsThresholdChanged: [];
    }>();

    // Stores
    const mainStore = useScheduleMainStore();
    const showScheduleStore = useShowScheduleStore();

    // Computed
    const conflictsArePresent = computed(() => showScheduleStore.conflictsArePresent);
    const editMode = computed(() => showScheduleStore.editMode);
    const matSchedules = computed(() => showScheduleStore.matSchedules);
    const matVisibility = computed(() => showScheduleStore.matVisibility);
    const allMatsSelected = computed(() => {
        const totalMatSchedules = Object.keys(matSchedules.value).length;
        if (totalMatSchedules === 0) {
            return false;
        }

        let allSelected = true;
        Object.keys(matVisibility.value).forEach(matId => {
            allSelected = allSelected && matVisibility.value[Number(matId)];
        });
        return allSelected;
    });

    const totalMats = computed(() => {
        return Object.keys(matSchedules.value).length;
    });

    const conflictsThreshold = computed({
        get() {
            return showScheduleStore.conflictsThreshold;
        },
        set(value: number) {
            showScheduleStore.conflictsThreshold = value;
        },
    });

    const multiRowMode = computed({
        get() {
            return showScheduleStore.multiRowMode;
        },
        set(value: boolean) {
            saveScheduleMatsMultiRowModeToLocalStorage(mainStore.eventId, value);
            showScheduleStore.multiRowMode = value;
        },
    });

    // Methods
    function openConflictsDetailsModal() {
        eventBus.$emit(ON_SHOW_CONFLICTS_MODAL);
    }

    function selectAllMats() {
        Object.keys(matSchedules.value).forEach(matId => {
            showScheduleStore.setMatVisibility(Number(matId), true);
        });
    }

    function deselectAllMats() {
        Object.keys(matSchedules.value).forEach(matId => {
            showScheduleStore.setMatVisibility(Number(matId), false);
        });
    }

    function onMatVisibilityUpdated() {
        saveVisibleMatsToLocalStorage(mainStore.eventId, matVisibility.value);
    }

    function onConflictsThresholdChange() {
        saveConflictsThresholdToLocalStorage(mainStore.eventId, conflictsThreshold.value);
        emit("conflictsThresholdChanged");
    }
</script>
